import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeatureRequest } from '../types'
import {
  deleteFeatureForCustomer,
  getEffectiveFeaturesForCustomer,
  getFeatures,
  getFeaturesForTemplate,
  getOverriddenFeaturesForCustomer,
  saveFeatureForCustomer,
  saveFeatureForTemplate,
} from './endpoints'

enum queryKeys {
  SUBSCRIPTION = 'subscription',
  FEATURES = 'features',
  EFFECTIVE_FEATURES = 'effective-features',
  DELETE_FEATURE = 'delete-feature',
  TEMPLATES = 'templates',
}

const queryKey = {
  effectiveFeatures: (customerId: string) => [queryKeys.EFFECTIVE_FEATURES, customerId] as const,
  deleteFeatureForCustomer: (customerId: string) => [queryKeys.DELETE_FEATURE, customerId] as const,
}

export const useGetFeaturesForTemplate = (templateId: string) =>
  useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES, templateId],
    queryFn: async () => getFeaturesForTemplate(templateId),
  })

export const useGetFeatures = () =>
  useQuery({
    queryKey: [queryKeys.TEMPLATES, queryKeys.FEATURES],
    queryFn: getFeatures,
  })

export const useSaveFeatureForTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ templateId, featureRequest }: { templateId: string; featureRequest: FeatureRequest<null> }) =>
      saveFeatureForTemplate(templateId, featureRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES],
      })
    },
  })
}

export const useSaveFeatureForCustomer = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES, customerId],
    mutationFn: ({ featureId, featureRequest }: { featureId: string; featureRequest: FeatureRequest<null> }) => {
      return saveFeatureForCustomer({ customerId, featureId, featureRequest })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES, customerId],
      })
    },
  })
}

interface UseFeaturesForCustomerParams {
  customerId: string | undefined
}

export const useOverriddenFeaturesForCustomer = ({ customerId }: UseFeaturesForCustomerParams) => {
  return useQuery({
    queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES, customerId],
    queryFn: async () => getOverriddenFeaturesForCustomer({ customerId: customerId as string }),
    enabled: !!customerId,
  })
}

export const useEffectiveFeaturesForCustomer = ({ customerId }: UseFeaturesForCustomerParams) => {
  return useQuery({
    queryKey: queryKey.effectiveFeatures(customerId as string),
    queryFn: async () => getEffectiveFeaturesForCustomer({ customerId: customerId as string }),
    enabled: !!customerId,
  })
}

export const useDeleteFeatureForCustomer = ({ customerId }: UseFeaturesForCustomerParams) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKey.deleteFeatureForCustomer(customerId as string),
    mutationFn: ({ featureId }: { featureId: string }) => deleteFeatureForCustomer({ customerId: customerId as string, featureId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.SUBSCRIPTION, queryKeys.FEATURES, customerId],
      })
    },
  })
}
